import cloneDeep from 'lodash.clonedeep';
import { AfWhitelist, AdaptedAfWhitelist } from 'data/afWhitelist/afWhitelist.types';
import {
  FormTemplate,
  FormTemplateField,
  FormTemplateOption,
  FormTemplateSection,
} from 'data/template/template.types';
import { DistributorData, Distributor } from 'data/distributor/distributor.types';
import { SupportedFeaturesAccessFunctions } from 'data/supportedFeatures/supportedFeatures.types';

export function adaptTemplateVisibilityMode(
  template: FormTemplate | undefined,
  mode: 'create' | 'edit' | 'view' | 'copy',
): FormTemplate | undefined {
  if (template == null) {
    return;
  }

  const clonedTemplate = cloneDeep(template);

  if (mode === 'edit') {
    (clonedTemplate.main as FormTemplateSection).fields.id.readOnly = true;
  } else if (mode === 'view') {
    setAllFieldsAsReadOnlyExcludingFields(clonedTemplate, []);
  }

  return clonedTemplate;
}

function setAllFieldsAsReadOnlyExcludingFields(template: FormTemplate, excludingFields: string[]) {
    Object.entries(template).forEach(([_templateKey, templateEntry]) => {
      if (typeof templateEntry === 'string') {
        return;
      }
      Object.entries(templateEntry.fields).forEach(([fieldKey, fieldEntry]) => {
        if (!excludingFields.includes(fieldKey)) {
          fieldEntry.readOnly = true;
        }
      });
    });
}

export function adaptTemplateOptions(
  template: FormTemplate | undefined,
  distributors: DistributorData,
  enabledAccessFunction: SupportedFeaturesAccessFunctions,
  availableAccessFunction: SupportedFeaturesAccessFunctions,
  formData?: AdaptedAfWhitelist
): FormTemplate | undefined {
  const clonedTemplate = cloneDeep(template);
  if (clonedTemplate) {
    const afTypeOptions = getAfTypeOptions(distributors, availableAccessFunction, enabledAccessFunction);
    setOptionForTemplateSectionField(clonedTemplate.main as FormTemplateSection, afTypeOptions, 'AFTYPE');
  }

  if (formData && clonedTemplate && clonedTemplate.main) {
    clonedTemplate.main.fields.AFTYPE.initial = formData.AFTYPE;
    clonedTemplate.main.fields.AFPROTOCOL.initial = formData.AFPROTOCOL;

    // Set Af protocol options
    const afProtocolOptions = getAfProtocolOptions(distributors, formData.AFTYPE);
    setOptionForTemplateSectionField(clonedTemplate.main as FormTemplateSection, afProtocolOptions, 'AFPROTOCOL');
  }
  return clonedTemplate;
}

export const getModuleOptions = (distributors: DistributorData, enabledAccessFunction: SupportedFeaturesAccessFunctions) => {
  const moduleNameSet: Set<string> = new Set<string>();

  distributors?.ss8Distributors.forEach(({ moduleName, afTags }) => {
    const shouldModuleNameBeAvailable = afTags.some((afTag) => !!enabledAccessFunction[afTag]);
    if (shouldModuleNameBeAvailable) {
      moduleNameSet.add(moduleName);
    }
  });

  return Array.from(moduleNameSet).map((moduleName) => {
    return {
      label: moduleName,
      value: moduleName,
    };
  }).sort((a, b) => a.label.toLocaleLowerCase().localeCompare(b.label.toLocaleLowerCase()));

};

export const getInstanceOptions = (distributors: DistributorData, moduleValue : string) => {
  const instanceOptions: FormTemplateOption[] = [];
  distributors?.ss8Distributors.forEach((distributor:Distributor) => {
    if (moduleValue === distributor.moduleName) {
      const { instanceId } = distributor;
      instanceOptions.push({
        label: `${instanceId}`,
        value: `${instanceId}`,
      });
    }
  });

  return instanceOptions;
}

export const getAfTypeOptions = (
  distributors: DistributorData,
  availableAccessFunction: SupportedFeaturesAccessFunctions,
  enabledAccessFunction: SupportedFeaturesAccessFunctions
) => {

  const afTypeOptions = Object.keys(enabledAccessFunction).map((key) => {
    return ({
      label: enabledAccessFunction[key].title,
      value: enabledAccessFunction[key].xcipioAFType
    })
  })
  return afTypeOptions.sort((item1, item2) => item1.label.localeCompare(item2.label));
}

export const getAfProtocolOptions = (distributors: DistributorData, afTypeValue: string) => {
  const selectedDistributor = distributors?.ss8Distributors?.find((distributor) => {
    return distributor.afTags.some((afTag) => afTag === afTypeValue)
  })
  const module = selectedDistributor?.moduleName ?? '';
  const key = getAfTagsToProtocolMapKey(afTypeValue, module, distributors);
  const protocolList = distributors?.afTagsToProtocolMap[key]?.afProtocols ?? [];
  return protocolList.map((protocol) => {
    return ({
      label: protocol,
      value: protocol
    });
  })
}

export function setOptionForTemplateSectionField(
  templateSection: FormTemplateSection,
  options: FormTemplateOption[],
  fieldName: string,
): void {
  const { fields } = templateSection;

  if (!fields) {
    return;
  }

  const templateField = fields[fieldName];
  if (templateField == null || templateField?.options == null) {
    return;
  }

  setOptionFormTemplateField(templateField, options);
}

function setOptionFormTemplateField(
    templateField: FormTemplateField,
    options: FormTemplateOption[],
  ) {
    const firstOption = templateField.options?.[0];
    if (firstOption) {
      templateField.options = [firstOption];
    }

    if (options.length > 0) {
      templateField.options?.push(...options);
      templateField.disabled = false;
    } else {
      templateField.disabled = true;
    }
}

export function getAfTagsToProtocolMapKey(type:string, moduleName:string|undefined, distributors:DistributorData): string {
  let key = `${type}#${moduleName}`;
  if (!distributors?.afTagsToProtocolMap[key]) {
    key = type;
  }
  return key;
}

export function adaptAfWhitelistData(
  afWhitelistData: AfWhitelist,
  distributors: DistributorData,
): AdaptedAfWhitelist {

  // find the MODULE and INSTANCE from AFTYPE
  const clonedAfWhitelistData: AdaptedAfWhitelist = cloneDeep({
    id: afWhitelistData.id,
    AFID: afWhitelistData.AFID,
    AFTYPE: afWhitelistData.AFTYPE,
    AFPROTOCOL: afWhitelistData.AFPROTOCOL,
    IPADDR: afWhitelistData.IPADDR,
    GRAYITEM: afWhitelistData.GRAYITEM === 'Y'
  });

  return clonedAfWhitelistData;
}
